// Lossless image compression utilities

import type {
  CompressionConfig,
  CompressionResult,
} from '../types/imageCompression';

/**
 * Compress image using canvas with lossless optimization
 */
export async function compressImageLossless(
  file: File | Blob,
  config: CompressionConfig
): Promise<CompressionResult> {
  const originalSize = file.size;

  // Check if compression is needed based on mode
  const shouldCompress = shouldCompressImage(originalSize, config);

  if (!shouldCompress) {
    // Return original without compression
    const dataUrl = await blobToDataUrl(file);
    return {
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1,
      dataUrl,
      wasCompressed: false,
      format: file.type,
    };
  }

  // Perform lossless compression
  const compressedDataUrl = await performLosslessCompression(file, config);
  const compressedSize = estimateBase64Size(compressedDataUrl);

  return {
    originalSize,
    compressedSize,
    compressionRatio: compressedSize / originalSize,
    dataUrl: compressedDataUrl,
    wasCompressed: true,
    format: getFormatFromDataUrl(compressedDataUrl),
  };
}

/**
 * Determine if image should be compressed based on config
 */
function shouldCompressImage(size: number, config: CompressionConfig): boolean {
  switch (config.mode) {
    case 'none':
      return false;
    case 'auto':
      return size > config.threshold;
    case 'always':
      return true;
    default:
      return false;
  }
}

/**
 * Perform lossless compression using canvas
 */
async function performLosslessCompression(
  file: File | Blob,
  config: CompressionConfig
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        try {
          const compressed = compressWithCanvas(img, config);
          resolve(compressed);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Compress image using canvas with optimal settings
 */
function compressWithCanvas(img: HTMLImageElement, config: CompressionConfig): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', {
    alpha: true,
    desynchronized: false,
  });

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Calculate dimensions while maintaining aspect ratio
  let { width, height } = calculateDimensions(
    img.width,
    img.height,
    config.maxWidth,
    config.maxHeight
  );

  canvas.width = width;
  canvas.height = height;

  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw image
  ctx.drawImage(img, 0, 0, width, height);

  // Try PNG first (lossless)
  const pngDataUrl = canvas.toDataURL('image/png');
  const pngSize = estimateBase64Size(pngDataUrl);

  // Try WebP (better compression, still high quality)
  const webpDataUrl = canvas.toDataURL('image/webp', config.quality);
  const webpSize = estimateBase64Size(webpDataUrl);

  // Try JPEG (for photos)
  const jpegDataUrl = canvas.toDataURL('image/jpeg', config.quality);
  const jpegSize = estimateBase64Size(jpegDataUrl);

  // Return the smallest format
  const formats = [
    { dataUrl: pngDataUrl, size: pngSize, name: 'PNG' },
    { dataUrl: webpDataUrl, size: webpSize, name: 'WebP' },
    { dataUrl: jpegDataUrl, size: jpegSize, name: 'JPEG' },
  ];

  const smallest = formats.reduce((prev, curr) => (curr.size < prev.size ? curr : prev));

  console.log(`Compressed using ${smallest.name}: ${(smallest.size / 1024).toFixed(2)}KB`);

  return smallest.dataUrl;
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const widthRatio = maxWidth / width;
  const heightRatio = maxHeight / height;
  const ratio = Math.min(widthRatio, heightRatio);

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Estimate size of base64 string in bytes
 */
function estimateBase64Size(dataUrl: string): number {
  const base64 = dataUrl.split(',')[1];
  return Math.ceil((base64.length * 3) / 4);
}

/**
 * Get format from data URL
 */
function getFormatFromDataUrl(dataUrl: string): string {
  const match = dataUrl.match(/^data:image\/(\w+);base64,/);
  return match ? match[1] : 'unknown';
}

/**
 * Batch compress multiple images
 */
export async function compressImagesBatch(
  files: (File | Blob)[],
  config: CompressionConfig,
  onProgress?: (current: number, total: number) => void
): Promise<CompressionResult[]> {
  const results: CompressionResult[] = [];

  for (let i = 0; i < files.length; i++) {
    const result = await compressImageLossless(files[i], config);
    results.push(result);

    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }

  return results;
}

/**
 * Get compression statistics
 */
export function getCompressionStats(results: CompressionResult[]): {
  totalOriginalSize: number;
  totalCompressedSize: number;
  totalSaved: number;
  averageRatio: number;
  compressedCount: number;
} {
  const totalOriginalSize = results.reduce((sum, r) => sum + r.originalSize, 0);
  const totalCompressedSize = results.reduce((sum, r) => sum + r.compressedSize, 0);
  const totalSaved = totalOriginalSize - totalCompressedSize;
  const averageRatio =
    results.reduce((sum, r) => sum + r.compressionRatio, 0) / results.length;
  const compressedCount = results.filter((r) => r.wasCompressed).length;

  return {
    totalOriginalSize,
    totalCompressedSize,
    totalSaved,
    averageRatio,
    compressedCount,
  };
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Validate image file
 */
export function isValidImageFile(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
  return validTypes.includes(file.type);
}

/**
 * Get optimal compression config based on image characteristics
 */
export async function getOptimalConfig(file: File): Promise<Partial<CompressionConfig>> {
  const img = await loadImage(file);

  // For large images, use more aggressive compression
  if (img.width > 3000 || img.height > 3000) {
    return {
      maxWidth: 2560,
      maxHeight: 1440,
      quality: 0.90,
    };
  }

  // For medium images
  if (img.width > 1920 || img.height > 1080) {
    return {
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.92,
    };
  }

  // For small images, minimal compression
  return {
    maxWidth: img.width,
    maxHeight: img.height,
    quality: 0.95,
  };
}

/**
 * Load image from file
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target?.result as string;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
